import {
  buildBucketFilter,
  buildFilter,
  buildRangeFilter,
  composeFilters,
  expandFeelings,
} from '@iheartjane/menu';
import { ObjectKeys } from '@iheartjane/utils';

import type { AlgoliaProduct, JaneSearchState } from '@jane/search/types';

interface BuildSearchFilterProps {
  initialStaticFilters?: string;
  searchState: JaneSearchState<AlgoliaProduct>;
  staticFilters?: string;
}

export const buildSearchFilter = ({
  initialStaticFilters,
  searchState,
  staticFilters,
}: BuildSearchFilterProps) => {
  const { filters, bucketFilters, rangeFilters } = searchState;
  const {
    applicable_special_ids,
    applicable_bulk_special_ids,
    ['applicable_bundle_special_ids.discount']:
      applicable_bundle_special_ids_discount,
    ['applicable_bundle_special_ids.qualify']:
      applicable_bundle_special_ids_qualify,
    applicable_cart_total_special_ids,
    ...baseFilters
  } = filters || {};
  const specialFilters = {
    applicable_special_ids,
    applicable_bulk_special_ids,
    ['applicable_bundle_special_ids.discount']:
      applicable_bundle_special_ids_discount,
    ['applicable_bundle_special_ids.qualify']:
      applicable_bundle_special_ids_qualify,
    applicable_cart_total_special_ids,
  };

  const hasSpecialFilters =
    [
      ...(applicable_bulk_special_ids ?? []),
      ...(applicable_bundle_special_ids_discount ?? []),
      ...(applicable_bundle_special_ids_qualify ?? []),
      ...(applicable_cart_total_special_ids ?? []),
      ...(applicable_special_ids ?? []),
    ].length > 0;

  const composedFilters = composeFilters(
    ...(staticFilters ? [staticFilters] : []),
    ...(initialStaticFilters ? [initialStaticFilters] : []),
    ...(baseFilters
      ? ObjectKeys(baseFilters).map((attribute) => {
          const values = baseFilters[attribute];
          // Expanded here, at the query boundary, rather than where the URL is
          // parsed, so the active-filter UI still shows the one value picked.
          return buildFilter(
            attribute,
            attribute === 'feelings' && values
              ? expandFeelings(values.map(String))
              : values
          );
        })
      : []),
    // We OR together the special filters so that they are treated as though they were one facet type
    ...(hasSpecialFilters
      ? [
          `(${ObjectKeys(specialFilters)
            .map((attribute) =>
              buildFilter(attribute, specialFilters[attribute])
            )
            .filter(Boolean)
            .join(' OR ')})`,
        ]
      : []),
    ...(rangeFilters
      ? ObjectKeys(rangeFilters).map((attribute) => {
          const value = rangeFilters[attribute];

          return buildRangeFilter(attribute, value?.min, value?.max);
        })
      : []),
    ...(bucketFilters
      ? ObjectKeys(bucketFilters).map((attribute) => {
          const value = bucketFilters[attribute];

          return buildBucketFilter(attribute, value);
        })
      : [])
  );

  return composedFilters;
};
